/* eslint-disable no-mixed-spaces-and-tabs */
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	Box,
	Button,
	CircularProgress,
	Container,
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import HtmlPreview from '../../HtmlPreview';
import EmailTemplateAutofill from '@/components/Drawers/AddEmailDrawer/EmailTemplateAutofill';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { showToast } from '@/features/toast/toastSlice';
import {
	adjustHtmlTablePadding,
	assignBlockIds,
	convertToHandlebarsTemplate,
	EMAIL_TEMPLATE_TYPES,
	EMPTY_HTML,
} from '../../../utils/emailHelper';
import {
	useGetEmailTemplatesQuery,
	useSaveEmailTemplateMutation,
} from '@/features/templates/templatesApi';
import EmailTemplateSelect from '../../selects/EmailTemplateSelect';
import testDealJson from '@/assets/json/test-deal.json';
import testClientJson from '@/assets/json/test-client.json';
import { TEMPLATE_GROUPS } from '@/features/templates/helper';
import validationSchema from '@/components/dialogs/SaveEmailTemplateDialog/validationSchema';
import FormikNoteObject from '../fields/FormikNoteObject';
import FolioRadioGroup from '@/components/FolioRadioGroup';
import DevJSONView from '@/components/DevJSONView';
import FormNavigationBlocker from '../FormNavigationBlocker';
import TiptapEditor from '@/components/TiptapEditor';
import { TEMPLATE_VALUE } from '@/components/TiptapEditor/Extensions/TemplateValue';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';

const SOA_ERROR_MESSAGE = 'Template must be set to Statement of Advice type due to dynamic fields.';

const bodyValidationSchema = Yup.object().shape({
	body: Yup.string().required('Email body is required.'),
});

const CreateEmailTemplateForm = () => {
	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const [showPreview, setShowPreview] = useState(false);

	const dispatch = useDispatch();

	const [loadingTemplate, setLoadingTemplate] = useState(false);

	const { data: existingTemplates } = useGetEmailTemplatesQuery(
		{ ownerDocId: userDetails?.userId },
		{ skip: !userDetails?.userId },
	);

	const initialValues = {
		body: '',
		errorMessage: '',
		client: {
			...(testClientJson?.clientRow ?? {}),
			brokerGroupId: userDetails?.brokerGroupId,
			ownerDocId: userDetails?.userId,
		},
		deal: {
			...(testDealJson ?? {}),
			brokerGroupId: userDetails?.brokerGroupId,
			ownerDocId: userDetails?.userId,
		},
		template: {},
		html: '',
		replaceExisting: false,
		name: '',
		type: '',
		shareIds: [],
		existingTemplates,
	};

	const [saveEmailTemplate] = useSaveEmailTemplateMutation();

	const [soaTypeError, setSoaTypeError] = useState(false);

	return (
		<Formik
			{...{
				initialValues,
				validateOnChange: false,
				validateOnBlur: true,
				validationSchema: validationSchema.concat(bodyValidationSchema),
				enableReinitialize: false,
				onSubmit: async (values, { resetForm, setFieldValue, setSubmitting }) => {
					setFieldValue('errorMessage', '');
					console.log('📋 ~ Formik values', values);
					console.log('📋 ~ Email html value', values.html);
					try {
						const newHtml = adjustHtmlTablePadding(values.html, values.headStyle);

						const { template: handlebarsTemplate, requirements } =
							convertToHandlebarsTemplate(newHtml);
						console.log('values.requirements', values.requirements);
						setFieldValue('requirements', requirements);

						console.log('values.requirements after', values.requirements);
						const soaType = requirements.includes('statementOfAdvice');

						if (soaType && values.type !== 'statementOfAdvice') {
							console.log(
								'🙅 ~ Error: template must be Statement of Advice type',
								values.type,
							);
							setSoaTypeError(true);
							dispatch(
								showToast({
									error: true,
									message: SOA_ERROR_MESSAGE,
								}),
							);
							setSubmitting(false);
							return;
						}
						setSoaTypeError(false);

						let replaceExisting = values.replaceExisting;
						if (
							values.replaceExisting &&
							(values.template?.master || !values.template?.canUpdate)
						) {
							replaceExisting = false;
						}

						const res = await saveEmailTemplate({
							type: values.type,
							html: handlebarsTemplate,
							name: values.name,
							userIds: [],
							brokerGroupIds: [],
							requirements,
							...(replaceExisting &&
								values.template?.id &&
								!values.template?.master && {
									docId: values.template.id,
									// name: values.name,
									// type: values.type,
								}),
						}).unwrap();

						console.log('Here is the res', res);
						if (res) {
							dispatch(
								showToast({
									message: 'Email template saved successfully!',
									autohide: true,
								}),
							);
						}
						resetForm();
						setFieldValue('successMessage', 'Email template saved successfully!');
					} catch (err) {
						console.log('🙅 ~ Error saving template', err);
						dispatch(
							showToast({
								error: true,
								message: 'Error saving template. Please try again.',
							}),
						);
					}
					setSubmitting(false);
					window.history.replaceState({}, '');
				},
			}}
		>
			{({
				isSubmitting,
				setFieldValue,
				values,
				handleSubmit,
				handleChange,
				touched,
				errors,
				getFieldProps,
				resetForm,
				validateField,
				validateForm,
				setErrors,
				setTouched,
			}) => {
				const validateAllFields = () => {
					const { requirements } = convertToHandlebarsTemplate(values.html);
					setFieldValue('requirements', requirements);
					setFieldValue('existingTemplates', existingTemplates);
					return validateForm().then((values) => {
						console.log('Validate form', values);
						if (Object.keys(values).length === 0) {
							return true;
						} else {
							const touched = Object.entries(values).reduce((acc, [key, val]) => {
								acc[key] = Boolean(val);
								return acc;
							}, {});
							setTouched(touched);
							setErrors(values);
							return false;
						}
					});
				};

				const handleDeleteBlock = (blockId) => {
					const parser = new DOMParser();
					const fullDoc = parser.parseFromString(values.html, 'text/html');

					const target = fullDoc.querySelector(`[data-id="${blockId}"]`);

					console.log('Target', target, 'parent node', target.parentNode);

					if (!target?.parentNode) return;

					const parent = target.parentElement;
					const templateValue = parent?.getAttribute('template-value');

					const grandparent = parent.parentNode;
					const gpTemplateValue = grandparent?.getAttribute('template-value');

					if (target.nodeName === 'TABLE' && (templateValue || gpTemplateValue)) {
						if (gpTemplateValue) {
							grandparent.remove();
						} else if (templateValue) {
							parent.remove();
						}
					} else if (parent) {
						parent?.removeChild(target);
					}
					setFieldValue('html', assignBlockIds(fullDoc.documentElement.innerHTML));

					console.log('Full doc after delete', fullDoc.documentElement.innerHTML);
				};

				return (
					<Form>
						<FormNavigationBlocker />
						<FormikErrorScroll {...{ isSubmitting }} />
						<Container>
							<Stack
								sx={{ width: '100%', height: '100%', pb: '2em' }}
								spacing={1}
								role='presentation'
							>
								<Stack direction='column' spacing={2}>
									<Typography variant='form_label'>
										{'Create/Update Email Templates'}
									</Typography>
									<FormHelperText>
										{
											'Please note that any changes to tables will not be reflected in saved templates at this point in time.'
										}
									</FormHelperText>
									<FormikNoteObject />
									<EmailTemplateAutofill
										setLoadingTemplate={setLoadingTemplate}
									/>
									<FormControl fullWidth sx={{ width: '100%' }}>
										<FormLabel>{'Use Existing Template'}</FormLabel>
										<EmailTemplateSelect
											value={values.template}
											setValue={(template) => {
												setFieldValue('template', template);
												if (!template) {
													setFieldValue('html', '');
												}
												if (template?.id) {
													setLoadingTemplate(true);
													setFieldValue('name', template.name);
													setFieldValue('type', template.type);
												}
												if (!values.existingTemplates) {
													setFieldValue(
														'existingTemplates',
														existingTemplates,
													);
												}
											}}
											// requirements={templateRequirements}
											// type={preloadData?.template}
										/>
									</FormControl>
									<FormControl fullWidth>
										<FolioRadioGroup
											name={'replaceExisting'}
											disabled={values.template?.master}
											value={values.replaceExisting}
											onChange={(e) => {
												const replaceExisting = ['true', true].includes(
													e.target.value,
												);
												setFieldValue('replaceExisting', replaceExisting);
												if (!values.replaceExisting) {
													setFieldValue(
														'existingTemplates',
														existingTemplates,
													);
												}
												if (replaceExisting) {
													setFieldValue('name', values.template?.name);
												}
											}}
											items={[
												{
													value: false,
													label: 'Create new',
												},
												{
													value: true,
													label: 'Replace existing',
													disabled:
														values.template?.master ||
														!values.template?.canUpdate ||
														!values.template?.id ||
														values.template?.sortBy ===
															TEMPLATE_GROUPS.shared,
												},
											]}
										/>
									</FormControl>
									<FormControl
										required
										error={touched.name && Boolean(errors.name)}
										fullWidth
										sx={{ width: '100%' }}
									>
										<FormLabel>{'Template name'}</FormLabel>
										<TextField
											fullWidth={true}
											name='name'
											placeholder='Template name'
											{...getFieldProps('name')}
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											disabled={isSubmitting || loadingTemplate}
											onChange={(e) => {
												const { onChange } = getFieldProps('name');
												if (!values.existingTemplates) {
													setFieldValue(
														'existingTemplates',
														existingTemplates,
													);
												}
												onChange(e);
											}}
											onBlur={(e) => {
												const { onBlur } = getFieldProps('name');
												onBlur(e);
												validateAllFields();
											}}
										/>
									</FormControl>
									<FormControl
										required
										error={touched.type && Boolean(errors.type)}
										fullWidth
										sx={{ width: '100%' }}
									>
										<FormLabel>{'Template type'}</FormLabel>
										<Select
											disabled={
												isSubmitting ||
												values.replaceExisting ||
												loadingTemplate ||
												values.template?.master ||
												values.template?.sortBy === TEMPLATE_GROUPS.shared
											}
											{...getFieldProps('type')}
											error={
												(touched.type && Boolean(errors.type)) ||
												soaTypeError
											}
										>
											{Object.entries(EMAIL_TEMPLATE_TYPES).map(
												([type, label], index) => (
													<MenuItem
														key={`email-template-${type}-${index}`}
														value={type}
													>
														{label}
													</MenuItem>
												),
											)}
										</Select>
										<>
											{(touched.type && Boolean(errors.type)) ||
												(soaTypeError && (
													<FormHelperText error>
														{soaTypeError
															? SOA_ERROR_MESSAGE
															: errors.type}
													</FormHelperText>
												))}
										</>
									</FormControl>
									<Box display='flex' justifyContent={'flex-end'}>
										<FormControlLabel
											control={
												<Switch
													checked={showPreview}
													disabled={loadingTemplate}
													onChange={(e) =>
														setShowPreview(e.target.checked)
													}
												/>
											}
											label={showPreview ? 'Edit email body' : 'Show preview'}
										/>
									</Box>
									<Stack sx={{ minHeight: '500px' }}>
										{loadingTemplate ? (
											<Box
												display='flex'
												justifyContent={'center'}
												alignItems={'center'}
												width='100%'
												height='6em'
											>
												<CircularProgress />
											</Box>
										) : showPreview ? (
											<HtmlPreview htmlValue={values.html} />
										) : (
											<TiptapEditor
												value={values.html}
												onChange={(html, content) => {
													// console.log('TIPTAP ON CHANGE', params);
													setFieldValue('html', html);
													setFieldValue('body', content.trim());
												}}
												onBlur={async () => {
													await validateField('body');
												}}
												error={touched.body && Boolean(errors.body)}
												disabled={isSubmitting}
												config={{
													toolbar: {
														user: {
															showSignature: false,
															show: true,
														},
														brokerage: { show: true },
														deal: {
															dealId: values.deal?.dealId,
															show: Boolean(values?.deal),
														},
														presetTable: {
															show:
																Boolean(values?.deal) &&
																Boolean(values?.client),
														},
														templateType: values.type,
														client: {
															clientId: values.client?.clientId,
															show: Boolean(values?.client),
														},
														containerBorder: {
															show: true,
														},
														align: { show: true },
														image: { show: true },
														table: {
															show: true,
														},
													},
													editable: (attr = {}) => !attr[TEMPLATE_VALUE],
												}}
											/>
										)}
									</Stack>

									{touched.body && Boolean(errors.body) && (
										<FormHelperText error>
											{touched.body && errors.body}
										</FormHelperText>
									)}
									{values.errorMessage.length > 0 && (
										<FormHelperText error>{values.errorMessage}</FormHelperText>
									)}
								</Stack>
								<Box justifyContent='center' display='flex'>
									<Button
										color='primary'
										variant='contained'
										disabled={isSubmitting}
										onClick={(e) => {
											e.preventDefault();
											validateAllFields().then((res) => {
												if (res) handleSubmit();
											});
										}}
									>
										{isSubmitting ? (
											<CircularProgress
												size='2em'
												sx={{ color: '#ffffff' }}
											/>
										) : values.replaceExisting ? (
											'Update Template'
										) : (
											'Save New Template'
										)}
									</Button>
								</Box>
							</Stack>
							<DevJSONView value={values} />
						</Container>
					</Form>
				);
			}}
		</Formik>
	);
};
export default CreateEmailTemplateForm;
